import React, { useState, useEffect, useCallback, useRef } from 'react';
import { projectsAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Plus, Loader2, FileText, Image, File, Trash2, Upload, FolderOpen, Download } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = [
  { value: 'plans', label: 'Plans' },
  { value: 'contracts', label: 'Contracts' },
  { value: 'permits', label: 'Permits' },
  { value: 'photos', label: 'Photos' },
  { value: 'invoices', label: 'Invoices' },
  { value: 'other', label: 'Other' },
];

const CAT_COLORS = {
  plans: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  contracts: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  permits: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  photos: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  invoices: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  other: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
};

function getFileIcon(name) {
  if (!name) return File;
  const ext = name.split('.').pop()?.toLowerCase();
  if (['jpg','jpeg','png','gif','webp','heic'].includes(ext)) return Image;
  if (['pdf','doc','docx','xls','xlsx','ppt','pptx','txt'].includes(ext)) return FileText;
  return File;
}

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function ProjectFiles({ projectId, canEdit }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [uploadCategory, setUploadCategory] = useState('other');
  const fileInputRef = useRef(null);

  const fetchFiles = useCallback(async () => {
    try {
      const params = {};
      if (categoryFilter) params.category = categoryFilter;
      const res = await projectsAPI.listFiles(projectId, params);
      setFiles(res.data.files || []);
    } catch {
      toast.error('Failed to load files');
    } finally {
      setLoading(false);
    }
  }, [projectId, categoryFilter]);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  const handleUpload = async (e) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    try {
      for (let i = 0; i < fileList.length; i++) {
        const f = fileList[i];
        const fd = new FormData();
        fd.append('file', f);
        fd.append('category', uploadCategory);
        await projectsAPI.uploadFile(projectId, fd);
      }
      toast.success(`${fileList.length} file${fileList.length > 1 ? 's' : ''} uploaded`);
      fetchFiles();
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await projectsAPI.deleteFile(projectId, fileId);
      fetchFiles();
      toast.success('File deleted');
    } catch { toast.error('Failed to delete file'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  // Group by category
  const grouped = {};
  files.forEach(f => {
    const cat = f.category || 'other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(f);
  });

  return (
    <div className="space-y-4" data-testid="project-files">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1 flex-wrap">
          <Button variant={!categoryFilter ? 'default' : 'ghost'} size="sm" onClick={() => setCategoryFilter('')} className="text-xs h-8">All ({files.length})</Button>
          {CATEGORIES.map(c => {
            const count = grouped[c.value]?.length || 0;
            if (count === 0 && categoryFilter) return null;
            return (
              <Button key={c.value} variant={categoryFilter === c.value ? 'default' : 'ghost'} size="sm" onClick={() => setCategoryFilter(categoryFilter === c.value ? '' : c.value)} className="text-xs h-8" data-testid={`file-filter-${c.value}`}>
                {c.label} {count > 0 && <span className="ml-1 opacity-60">({count})</span>}
              </Button>
            );
          })}
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <Select value={uploadCategory} onValueChange={setUploadCategory}>
              <SelectTrigger className="w-[120px] h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
            <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleUpload} />
            <Button size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-file-btn">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
              Upload
            </Button>
          </div>
        )}
      </div>

      {files.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground" data-testid="files-empty">
          <FolderOpen className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-lg font-medium">No files yet</p>
          <p className="text-sm mt-1">Upload plans, contracts, permits, and photos for this project.</p>
        </div>
      ) : (
        <div className="border rounded-lg divide-y" data-testid="files-list">
          {files.map(file => {
            const Icon = getFileIcon(file.file_name);
            const catColor = CAT_COLORS[file.category] || CAT_COLORS.other;
            const isImage = file.file_type === 'image';
            return (
              <div key={file.id} className="flex items-center gap-3 p-3 hover:bg-muted/20" data-testid={`file-row-${file.id}`}>
                <div className="w-10 h-10 rounded bg-muted/50 flex items-center justify-center flex-shrink-0">
                  {isImage && file.file_url ? (
                    <img src={file.file_url} alt="" className="w-10 h-10 rounded object-cover" />
                  ) : (
                    <Icon className="w-5 h-5 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{file.file_name}</p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                    <Badge variant="secondary" className={`text-xs ${catColor}`}>{file.category}</Badge>
                    {file.file_size && <span>{formatSize(file.file_size)}</span>}
                    {file.uploader_name && <span>by {file.uploader_name}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {file.file_url && (
                    <a href={file.file_url} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground p-1">
                      <Download className="w-4 h-4" />
                    </a>
                  )}
                  {canEdit && (
                    <button onClick={() => handleDelete(file.id)} className="text-muted-foreground hover:text-red-500 p-1" data-testid={`delete-file-${file.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
